// NEW FILE: apps/web/src/tier/user-agreement.ts

import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '@/core/firebase'
import { currentUser } from '@/core/state'

/**
 * Checks if the user has agreed to the terms.
 * If not, shows the agreement modal.
 * Returns true if the user is allowed to proceed (has agreed or just agreed).
 * Returns false if the user cancelled.
 */
export async function checkAndShowUserAgreement(): Promise<boolean> {
  if (!currentUser) return false

  // 1. Check Firestore status
  try {
    const userDocRef = doc(db, 'users', currentUser.uid)
    const userSnap = await getDoc(userDocRef)
    
    // If user already agreed, skip everything and return true
    if (userSnap.exists() && userSnap.data().hasAcceptedTerms) {
      return true
    }
  } catch (error) {
    console.error('Error checking agreement status:', error)
  }

  // 2. Show the Agreement Modal if they haven't agreed
  return new Promise<boolean>((resolve) => {
    const modalId = 'user-agreement-modal'
    let modal = document.getElementById(modalId)
    if (modal) modal.remove()

    // Placeholder URL for the document
    const documentUrl = 'https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf'
    const documentName = 'Finalytics_User_Agreement.pdf'

    modal = document.createElement('div')
    modal.id = modalId
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm'
    
    modal.innerHTML = `
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col overflow-hidden">
        <div class="p-6 border-b">
          <h3 class="text-xl font-bold text-gray-900">User Agreement</h3>
          <p class="text-sm text-gray-500 mt-1">Please review and accept the terms before proceeding.</p>
        </div>
        
        <div class="p-6 space-y-4">
          <div class="bg-gray-50 p-4 rounded-md border text-sm text-gray-600 h-32 overflow-y-auto">
            <p><strong>Terms of Service & Non-Disclosure Agreement</strong></p>
            <p class="mt-2">By proceeding, you agree to the Finalytics Terms of Service. You acknowledge that you are purchasing a commercial subscription.</p>
            <p class="mt-2">You also agree to the confidentiality clauses regarding the proprietary data analysis methods used in this application.</p>
            <p class="mt-2">Clicking "I Agree" will download the full legal document for your records.</p>
          </div>

          <div class="flex items-start">
            <div class="flex items-center h-5">
              <input id="agreement-checkbox" type="checkbox" class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded">
            </div>
            <div class="ml-3 text-sm">
              <label for="agreement-checkbox" class="font-medium text-gray-700">I have read and agree to the User Agreement</label>
              <p class="text-gray-500">The document will download automatically upon submission.</p>
            </div>
          </div>
        </div>

        <div class="p-6 border-t bg-gray-50 flex justify-end gap-3">
          <button id="agreement-cancel" class="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
            Cancel
          </button>
          <button id="agreement-submit" disabled class="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
            Submit & Download
          </button>
        </div>
      </div>
    `
    document.body.appendChild(modal)

    const checkbox = document.getElementById('agreement-checkbox') as HTMLInputElement
    const submitBtn = document.getElementById('agreement-submit') as HTMLButtonElement
    const cancelBtn = document.getElementById('agreement-cancel') as HTMLButtonElement

    checkbox.addEventListener('change', () => {
      submitBtn.disabled = !checkbox.checked
    })

    cancelBtn.addEventListener('click', () => {
      modal?.remove()
      resolve(false)
    })

    submitBtn.addEventListener('click', async () => {
      if (!currentUser) return

      submitBtn.disabled = true
      submitBtn.textContent = 'Processing...'
      
      try {
        const userDocRef = doc(db, 'users', currentUser.uid)
        await setDoc(userDocRef, { 
          hasAcceptedTerms: true,
          termsAcceptedAt: new Date() 
        }, { merge: true })

        const link = document.createElement('a')
        link.href = documentUrl
        link.download = documentName
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        modal?.remove()
        resolve(true)
      } catch (error) {
        console.error('Error saving agreement:', error)
        alert('An error occurred. Please try again.')
        submitBtn.disabled = false
        submitBtn.textContent = 'Submit & Download'
      }
    })
  })
}